'use client';

import { useOnClickOutside } from 'usehooks-ts';
import { useState, type RefObject, type KeyboardEventHandler } from 'react';

interface IUseEditableParams<T extends HTMLElement = HTMLElement> {
  onUpdate?: () => void;
  ref: RefObject<T | null>;
}

interface IUseEditableReturnProps<T extends HTMLElement = HTMLElement> {
  edit: () => void;
  isEditing: boolean;
  onKeyDown: KeyboardEventHandler<T>;
}

export function useEditable<T extends HTMLElement = HTMLElement>({
  ref,
  onUpdate,
}: IUseEditableParams<T>): IUseEditableReturnProps<T> {
  const [isEditing, setIsEditing] = useState(false);

  const handleUpdate = () => {
    setIsEditing(false);

    onUpdate?.();
  };

  useOnClickOutside(ref as RefObject<HTMLElement>, handleUpdate);

  const edit = () => {
    setIsEditing(true);
  };

  const onKeyDown: KeyboardEventHandler<T> = (event) => {
    if (event.key === 'Enter') {
      handleUpdate();
    } else if (event.key === 'Escape') {
      setIsEditing(false);
    }
  };

  return {
    edit,
    isEditing,
    onKeyDown,
  };
}
